import React, { useState, useEffect } from "react";
import { X, Share2, UserPlus, Users, Trash2, Shield } from "lucide-react";
import useFinance from "../../../hooks/useFinance";
import PermissionBadge from "../Common/PermissionBadge";

export default function ShareTableModal({
  isOpen,
  onClose,
  tableId,
  tableName,
  onUpdate,
}) {
  const {
    fetchGlobalPermissions,
    fetchTablePermissions,
    shareTable,
    updateShareLevel,
    removeShare,
  } = useFinance();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedPermission, setSelectedPermission] = useState("view");
  const [activeTab, setActiveTab] = useState("current"); // current | add

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all users
      const globalResult = await fetchGlobalPermissions();
      if (globalResult.ok) {
        setAllUsers(globalResult.permissions);
      }

      // Load table permissions
      const tableResult = await fetchTablePermissions(tableId);
      if (tableResult.ok) {
        setSharedUsers(tableResult.permissions);
      }
    } catch (error) {
      console.error("Failed to load permissions:", error);
    } finally {
      setLoading(false);
    }
  };

  const getAvailableUsers = () => {
    const sharedUserIds = new Set(sharedUsers.map((u) => u.user_id));
    return allUsers.filter((u) => !sharedUserIds.has(u.user_id) && !u.is_me);
  };

  const handleAddUsers = async () => {
    if (selectedUsers.length === 0) return;

    setSubmitting(true);
    try {
      const shares = selectedUsers.map((userId) => ({
        user_id: userId,
        permission: selectedPermission,
      }));

      const result = await shareTable({ table_id: tableId, shares });

      if (result.ok) {
        window.showToast?.("사용자가 추가되었습니다.", "success");
        setSelectedUsers([]);
        await loadData();
        onUpdate();
      } else if (result.denied) {
        window.showToast?.("공유 권한이 없습니다.", "error");
      } else {
        window.showToast?.("사용자 추가에 실패했습니다.", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePermission = async (userId, newPermission) => {
    setSubmitting(true);
    try {
      const result = await updateShareLevel({
        table_id: tableId,
        shares: [{ user_id: userId, permission: newPermission }],
      });

      if (result.ok) {
        window.showToast?.("권한이 변경되었습니다.", "success");
        await loadData();
        onUpdate();
      } else if (result.denied) {
        window.showToast?.("권한 변경 권한이 없습니다.", "error");
      } else {
        window.showToast?.("권한 변경에 실패했습니다.", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm("정말로 이 사용자의 접근 권한을 제거하시겠습니까?"))
      return;

    setSubmitting(true);
    try {
      const result = await removeShare({
        table_id: tableId,
        remove_ids: [userId],
      });

      if (result.ok) {
        window.showToast?.("사용자가 제거되었습니다.", "success");
        await loadData();
        onUpdate();
      } else if (result.denied) {
        window.showToast?.("제거 권한이 없습니다.", "error");
      } else {
        window.showToast?.("사용자 제거에 실패했습니다.", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const availableUsers = getAvailableUsers();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-4 rounded-t-xl flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Share2 className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-bold">테이블 공유 관리</h2>
                <p className="text-sm text-green-100 mt-1">{tableName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body with horizontal layout */}
        <div className="flex flex-1 min-h-0">
          {/* Left side - Current shared users */}
          <div className="w-1/2 border-r border-gray-200 flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  현재 공유 사용자
                </h3>
                <span className="text-sm text-gray-500">
                  ({sharedUsers.length}명)
                </span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                </div>
              ) : (
                <div className="space-y-3">
                  {sharedUsers.map((user) => (
                    <div
                      key={user.user_id}
                      className={`border rounded-lg p-4 ${
                        user.is_me
                          ? "border-green-300 bg-green-50"
                          : "border-gray-200"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-medium">
                              {user.user_name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">
                              {user.user_name}
                              {user.is_me && (
                                <span className="ml-2 text-sm text-green-600">
                                  (나)
                                </span>
                              )}
                            </h4>
                            <p className="text-sm text-gray-500">
                              ID: {user.user_id}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-10">
                          <PermissionBadge
                            type="table"
                            level={user.permission_type}
                          />

                          {!user.is_me && (
                            <>
                              <select
                                value={user.permission_type}
                                onChange={(e) =>
                                  handleUpdatePermission(
                                    user.user_id,
                                    e.target.value
                                  )
                                }
                                disabled={submitting}
                                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
                              >
                                <option value="admin">관리자</option>
                                <option value="edit">편집자</option>
                                <option value="view">조회자</option>
                              </select>

                              <button
                                onClick={() => handleRemoveUser(user.user_id)}
                                disabled={submitting}
                                className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors disabled:opacity-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {sharedUsers.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>아직 공유된 사용자가 없습니다.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right side - Add new users */}
          <div className="w-1/2 flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  사용자 추가
                </h3>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Permission Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    부여할 권한 선택
                  </label>
                  <div className="grid grid-cols-1 gap-3">
                    {[
                      {
                        value: "view",
                        label: "조회자",
                        desc: "데이터 조회만 가능",
                      },
                      {
                        value: "edit",
                        label: "편집자",
                        desc: "데이터 편집 가능",
                      },
                      {
                        value: "admin",
                        label: "관리자",
                        desc: "테이블 설정 변경 가능",
                      },
                    ].map((option) => (
                      <label
                        key={option.value}
                        className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedPermission === option.value
                            ? "border-green-500 bg-green-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name="permission"
                          value={option.value}
                          checked={selectedPermission === option.value}
                          onChange={(e) =>
                            setSelectedPermission(e.target.value)
                          }
                          className="sr-only"
                        />
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Shield className="w-4 h-4 text-gray-600" />
                            <span className="font-medium text-gray-900">
                              {option.label}
                            </span>
                          </div>
                          <span className="text-sm text-gray-500">
                            {option.desc}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* User Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    추가할 사용자 선택
                  </label>
                  <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {availableUsers.map((user) => (
                      <label
                        key={user.user_id}
                        className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          value={user.user_id}
                          checked={selectedUsers.includes(user.user_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUsers([
                                ...selectedUsers,
                                user.user_id,
                              ]);
                            } else {
                              setSelectedUsers(
                                selectedUsers.filter(
                                  (id) => id !== user.user_id
                                )
                              );
                            }
                          }}
                          className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <div className="flex-1">
                          <span className="font-medium text-gray-900">
                            {user.user_name}
                          </span>
                          <span className="ml-2 text-sm text-gray-500">
                            ({user.user_id})
                          </span>
                        </div>
                        <PermissionBadge
                          type="global"
                          level={user.access_level}
                        />
                      </label>
                    ))}

                    {availableUsers.length === 0 && (
                      <p className="text-center py-4 text-gray-500 text-sm">
                        추가 가능한 사용자가 없습니다.
                      </p>
                    )}
                  </div>
                </div>

                {/* Add Button */}
                <button
                  onClick={handleAddUsers}
                  disabled={selectedUsers.length === 0 || submitting}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  {submitting
                    ? "추가 중..."
                    : `선택한 ${selectedUsers.length}명 추가`}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
